import { useState } from 'react';
import { Check, Copy, Languages, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils/cn';
import { useTranscribeAudio } from '../api';
import { type PromptRow, type TranscribeLanguage } from '../types';

interface Props {
  /** Voice Files rows pass a PromptRow; the TTS-flow preview passes the same fields. */
  prompt: Pick<PromptRow, 'promptId' | 'fileName' | 'promptUrl'> | null;
  onOpenChange: (open: boolean) => void;
}

const LANGUAGES: { value: TranscribeLanguage; label: string; native: string }[] = [
 { value: 'hi', label: 'Hindi', native: 'हिन्दी' },
  // TODO: enable once the transcription service returns English (or GOOGLE_TRANSLATE_KEY is set).
  // { value: 'en', label: 'English', native: 'English' },
];

export function TranscribeDialog({ prompt, onOpenChange }: Props) {
  const transcribe = useTranscribeAudio();
  const [lang, setLang] = useState<TranscribeLanguage>('hi');
  const [results, setResults] = useState<Partial<Record<TranscribeLanguage, string>>>({});
  const [copied, setCopied] = useState(false);

  const text = results[lang];
  const loading = transcribe.isPending && transcribe.variables?.target === lang;
  const current = LANGUAGES.find((l) => l.value === lang)!;

  const reset = () => {
    setResults({});
    setLang('hi');
    setCopied(false);
    transcribe.reset();
  };

  const handleTranscribe = () => {
    if (!prompt || !Number.isFinite(prompt.promptId)) return toast.error('Prompt not found.');
    const target = lang;
    transcribe.mutate(
      { promptId: prompt.promptId, gender: 'MALE', target },
      {
        onSuccess: (res) => {
          const out = target === 'hi' ? res.hindiText : res.englishText;
          if (res.status === 'success' && out) {
            setResults((r) => ({ ...r, [target]: out }));
          } else {
            toast.error(res.msg ?? 'Could not transcribe.');
          }
        },
        onError: (err) =>
          toast.error(
            (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
              'Could not transcribe.',
          ),
      },
    );
  };

  const handleCopy = async () => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Could not copy.');
    }
  };

  return (
    <Dialog
      open={Boolean(prompt)}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-md overflow-hidden p-0">
        {/* Header */}
        <DialogHeader className="border-b border-border bg-gradient-to-br from-sky-500/10 via-violet-500/10 to-fuchsia-500/10 px-5 py-4">
          <DialogTitle className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-sky-500 to-violet-500 text-white shadow-sm">
              <Languages className="h-5 w-5" />
            </span>
            <span className="text-lg font-semibold tracking-tight">Transcript</span>
          </DialogTitle>
          <DialogDescription className="sr-only">
            Choose Hindi or English and transcribe this prompt.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 px-5 pb-1 pt-4">
          {/* Language */}
          <Select
            value={lang}
            onValueChange={(v) => {
              setLang(v as TranscribeLanguage);
              setCopied(false);
            }}
          >
            <SelectTrigger className="h-10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LANGUAGES.map((l) => (
                <SelectItem key={l.value} value={l.value}>
                  {l.native}
                  {l.native !== l.label && (
                    <span className="ml-2 text-xs text-muted-foreground">{l.label}</span>
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Transcript card */}
          <div
            className={cn(
              'relative overflow-hidden rounded-xl border bg-gradient-to-b from-card to-muted/40',
              text ? 'border-primary/30 shadow-sm' : 'border-dashed border-border',
            )}
          >
            <div className="flex items-center justify-between border-b border-border/60 px-3 py-1.5">
              <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                <Sparkles className="h-3 w-3 text-violet-500" />
                {current.native}
              </span>
              {text && (
                <button
                  type="button"
                  onClick={handleCopy}
                  className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              )}
            </div>
            <div className="max-h-48 min-h-[7rem] overflow-y-auto px-3 py-2.5">
              {loading ? (
                <div className="space-y-2" aria-label="Transcribing">
                  <div className="h-3 w-11/12 animate-pulse rounded bg-muted" />
                  <div className="h-3 w-full animate-pulse rounded bg-muted" />
                  <div className="h-3 w-3/4 animate-pulse rounded bg-muted" />
                </div>
              ) : text ? (
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">{text}</p>
              ) : (
                <div className="flex h-full min-h-[5.5rem] flex-col items-center justify-center gap-1 text-center text-xs text-muted-foreground">
                  <Languages className="h-5 w-5 opacity-40" />
                  Pick a language and press Transcribe
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="px-5 pb-4 pt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button onClick={handleTranscribe} disabled={transcribe.isPending || !prompt}>
            {transcribe.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {text ? 'Transcribe again' : 'Transcribe'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}